import * as React from 'react'
import { BookingInvoices } from './BookingInvoices'
import { MoreMenu } from './ui'
import { BookingExtensions, type BookingAction } from './BookingDetails'
import { BookingAssignmentDialog, type AssignmentBooking } from './BookingAssignmentDialog'
import { BookingExtensionForm } from './BookingExtensionForm'
import { BookingDatesForm } from './BookingDatesForm'
import { type OpsExtension } from './operationsModel'

type Booking = AssignmentBooking & {
  state: string
  hire_end_date: string
  operational_hire_end_date?: string | null
  inventory_allocations?: Array<{ state: string; inventory_units: { id: string } | null }>
  ops_extensions?: OpsExtension[]
}

export function LiveBookingOperations({ booking, canWrite, onSaved, children }: {
  booking: Booking
  canWrite: boolean
  onSaved: () => Promise<void>
  children?: (actions: BookingAction[], extensions: React.ReactNode) => React.ReactNode
}) {
  const [assigning, setAssigning] = React.useState(false)
  const [editingDates, setEditingDates] = React.useState(false)
  const [extension, setExtension] = React.useState<OpsExtension | 'new' | null>(null)
  const editable = canWrite && ['confirmed', 'unassigned'].includes(booking.state) && ['paid', 'partially_refunded'].includes(booking.payment_state)
  const extensions = [...(booking.ops_extensions || [])].sort((a, b) => a.created_at.localeCompare(b.created_at) || a.id.localeCompare(b.id))
  const saved = async () => { setAssigning(false); setEditingDates(false); setExtension(null); await onSaved() }

  const actions: BookingAction[] = editable ? [
    { label: 'Edit dates', onClick: () => setEditingDates(true) },
    { label: booking.state === 'unassigned' ? 'Assign units' : 'Change units', onClick: () => setAssigning(true) },
    { label: 'Add extension', onClick: () => setExtension('new') },
    ...extensions.map((item, index) => ({ label: `Edit extension ${index + 1}`, onClick: () => setExtension(item) })),
  ] : []
  return <>
    {children ? children(actions, <BookingExtensions extensions={extensions} renderInvoice={item => <BookingInvoices target={{ kind: 'extension', id: item.id }} canWrite={canWrite} unpaid={item.payment_status === 'unpaid'} />} />) : <section aria-label="Live booking operations">
      {actions.length > 0 && <MoreMenu label="Booking actions" items={actions} />}
      <BookingExtensions extensions={extensions} renderInvoice={item => <BookingInvoices target={{ kind: 'extension', id: item.id }} canWrite={canWrite} unpaid={item.payment_status === 'unpaid'} />} />
    </section>}
    {editingDates && editable && <BookingDatesForm booking={booking} onClose={() => setEditingDates(false)} onSaved={saved} />}
    {assigning && editable && <BookingAssignmentDialog booking={booking} change={booking.state === 'confirmed'}
      initialIds={(booking.inventory_allocations || []).flatMap(a => a.state === 'confirmed' && a.inventory_units ? [a.inventory_units.id] : [])}
      onClose={() => setAssigning(false)} onSaved={saved} />}
    {extension && editable && <BookingExtensionForm booking={{
      source: 'online', source_id: booking.id, reference: booking.booking_reference,
      effective_end_date: booking.operational_hire_end_date || booking.hire_end_date,
      return_date: booking.operational_collection_date || booking.collection_date, updated_at: booking.updated_at,
    }} extension={extension === 'new' ? undefined : extension} onClose={() => setExtension(null)} onSaved={saved} />}
  </>
}
